import { createLogger } from "@/logger";

const log = createLogger("storage");

export type RetryOptions = {
  maxRetries?: number;
  initialDelayMs?: number;
  shouldRetry?: (error: unknown) => boolean;
  label?: string;
};

const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_INITIAL_DELAY_MS = 500;

/**
 * Run `fn` with exponential backoff between attempts. Retries are bounded by
 * `maxRetries` (the first call is attempt 0, so up to `maxRetries + 1` total
 * invocations). Use `shouldRetry` to opt out for non-transient failures
 * (e.g. QuotaExceededError) so we surface them immediately instead of waiting.
 */
export async function retryWithBackoff<T>(fn: () => Promise<T>, options: RetryOptions = {}): Promise<T> {
  const maxRetries = options.maxRetries ?? DEFAULT_MAX_RETRIES;
  const initialDelayMs = options.initialDelayMs ?? DEFAULT_INITIAL_DELAY_MS;
  const shouldRetry = options.shouldRetry ?? (() => true);
  const label = options.label ?? "operation";

  let lastError: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt >= maxRetries || !shouldRetry(error)) break;

      const delayMs = initialDelayMs * 2 ** attempt;
      log.warn("[StorageRetry] Attempt failed, retrying", {
        label,
        attempt: attempt + 1,
        delayMs,
        error,
      });
      await sleep(delayMs);
    }
  }

  throw lastError;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
